/**
 * Generate a full primary color palette (50–900) from a single hex color.
 * The input hex is treated as the 600 shade (main brand color).
 * Lighter shades are created by mixing with white, darker by mixing with black.
 *
 * The palette reaches Tailwind as CSS variables holding each shade's CHANNELS
 * (`--color-primary-600-rgb: 61 90 128`), not a finished colour. tailwind.config.js
 * wraps them as rgb(var(--color-primary-600-rgb, …) / <alpha-value>), which is what lets
 * a class carry an opacity (bg-primary-50/40). See the note there for what happened
 * while the variables held hex colours instead.
 */

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const cleaned = hex.replace('#', '');
  const num = parseInt(cleaned, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0'))
      .join('')
  );
}

function mixColors(
  color: { r: number; g: number; b: number },
  target: { r: number; g: number; b: number },
  amount: number
): { r: number; g: number; b: number } {
  return {
    r: color.r + (target.r - color.r) * amount,
    g: color.g + (target.g - color.g) * amount,
    b: color.b + (target.b - color.b) * amount,
  };
}

interface PrimaryPalette {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

function generatePalette(hexColor: string): PrimaryPalette {
  const base = hexToRgb(hexColor);
  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };

  const shade = (target: { r: number; g: number; b: number }, amount: number) => {
    const mixed = mixColors(base, target, amount);
    return rgbToHex(mixed.r, mixed.g, mixed.b);
  };

  return {
    50: shade(white, 0.92),
    100: shade(white, 0.82),
    200: shade(white, 0.65),
    300: shade(white, 0.45),
    400: shade(white, 0.25),
    500: shade(white, 0.1),
    600: hexColor,
    700: shade(black, 0.15),
    800: shade(black, 0.3),
    900: shade(black, 0.45),
  };
}

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

/** '#3D5A80', '3d5a80' or '#abc' -> '#3d5a80' / '#aabbcc'; null for anything else. */
function normaliseHex(input: string): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(input ?? '').trim());
  if (!m) return null;
  const digits = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
  return `#${digits.toLowerCase()}`;
}

/** '#3d5a80' -> '61 90 128', the form rgb() takes before a "/ alpha". */
function hexToChannels(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  return `${r} ${g} ${b}`;
}

/**
 * Apply a primary color palette as CSS custom properties on :root.
 */
export function applyThemePalette(hexColor: string): void {
  // Anything but a 3- or 6-digit hex colour would come out as nonsense channels, and a
  // nonsense channel triple is no brand colour at all. Leave the stylesheet's own
  // defaults in place instead.
  const hex = normaliseHex(hexColor);
  if (!hex) {
    clearThemePalette();
    return;
  }
  const palette = generatePalette(hex);
  const root = document.documentElement;

  for (const shade of SHADES) {
    root.style.setProperty(`--color-primary-${shade}-rgb`, hexToChannels(palette[shade]));
  }
}

/**
 * Remove custom theme palette (revert to Tailwind defaults).
 */
export function clearThemePalette(): void {
  const root = document.documentElement;
  for (const shade of SHADES) {
    root.style.removeProperty(`--color-primary-${shade}-rgb`);
  }
}
